/**
 * ストック（approved）・予約済み（scheduled）・投稿中（publishing）の一覧（audit C1）。
 * 従来 approved/scheduled はどの画面にも出ず「承認した投稿が消えた」ように見えていた。
 * ストックを自動で投稿する定期ルールの設定画面はまだ無い（Phase 2）ため、ここから投稿・予約する。
 */

import { useState } from 'react'
import { Loader2 } from 'lucide-react'
import type { QueueItem } from '../../lib/postTypes'
import { formatSchedule } from '../../lib/datetime'
import { InlineError } from '../../components/QueryState'
import { useQueueActions, type Notify } from './useQueueActions'
import { PostBody } from './PostBody'
import { ChannelRows } from './ChannelRows'
import { ScheduleForm } from './ScheduleForm'
import { RejectButton } from './RejectButton'

export function StockSection({ stock, publishing, notify }: {
  stock: QueueItem[]
  publishing: QueueItem[]
  notify: Notify
}) {
  return (
    <section id="stock" className="scroll-mt-4 space-y-3" aria-labelledby="stock-heading">
      <h2 id="stock-heading" className="text-lg font-bold">ストック・予約済み（<span className="gm-num">{stock.length}</span>件）</h2>
      <p className="text-xs text-ink-muted">
        ストックの投稿は、ここで「今すぐ投稿」するか日時を予約するまで投稿されません（定期投稿ルールの設定は準備中です）。
      </p>
      {stock.length === 0 && publishing.length === 0 && (
        <p className="text-sm text-ink-muted">ストック・予約済みの投稿はありません。承認待ちの投稿を「承認（ストックへ）」するとここに並びます。</p>
      )}
      {publishing.map(item => <PublishingCard key={item.post.id} item={item} notify={notify} />)}
      {stock.map(item => <StockCard key={item.post.id} item={item} notify={notify} />)}
    </section>
  )
}

function StockCard({ item, notify }: { item: QueueItem; notify: Notify }) {
  const { post, channels } = item
  const actions = useQueueActions(item, notify)
  const { approve, reject, publish, pending, error } = actions
  const [scheduleOpen, setScheduleOpen] = useState(false)
  const scheduled = post.status === 'scheduled'
  const hasUnresolved = channels.some(c => c.unresolved)

  return (
    <div className="gm-card p-4">
      <PostBody item={item}
        badge={scheduled
          ? <span className="gm-chip gm-chip-info gm-num shrink-0">予約 {formatSchedule(post.scheduledAt)}</span>
          : <span className="gm-chip gm-chip-neutral shrink-0">ストック（未投稿）</span>} />
      <ChannelRows channels={channels} actions={actions} readOnly />
      <div className="mt-3 flex flex-wrap gap-2">
        <button disabled={pending || hasUnresolved} onClick={() => publish.mutate()}
          title={hasUnresolved ? '結果が不明な媒体を先に確認してください' : undefined}
          className="gm-btn gm-btn-secondary">今すぐ投稿</button>
        <button onClick={() => setScheduleOpen(o => !o)} aria-expanded={scheduleOpen}
          className="gm-btn gm-btn-secondary">{scheduled ? '日時を変更' : '日時を予約'}</button>
        <RejectButton disabled={pending} onConfirm={() => reject.mutate()} className="ml-auto" />
      </div>
      <InlineError message={error?.message} issues={error?.issues} className="mt-2" />
      {scheduleOpen && (
        <ScheduleForm initialIso={post.scheduledAt} disabled={pending}
          submitLabel={scheduled ? '変更する' : '予約する'}
          onSubmit={iso => approve.mutate({ scheduledAt: iso }, { onSuccess: () => setScheduleOpen(false) })} />
      )}
    </div>
  )
}

/** 投稿中は操作できない（読み取り専用）。結果不明の媒体の解決だけは出す */
function PublishingCard({ item, notify }: { item: QueueItem; notify: Notify }) {
  const actions = useQueueActions(item, notify)
  return (
    <div className="gm-card p-4">
      {/* 投稿の送信は AI 作業ではないので単色の進行表示（gm-ai-ring は使わない） */}
      <PostBody item={item}
        badge={<span className="gm-chip gm-chip-info shrink-0">
          <Loader2 size={12} className="motion-safe:animate-spin" aria-hidden />
          投稿中
        </span>} />
      <ChannelRows channels={item.channels} actions={actions} readOnly />
      <InlineError message={actions.error?.message} issues={actions.error?.issues} className="mt-2" />
    </div>
  )
}
